import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Moment } from 'moment';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { notification } from 'antd';

import type {
  IWorkJourneyManagementUser,
  IWorkJourneyReplacementCandidate,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { userHasAttendanceAddress } from '@src/packages/users/screens/UsersList/utils/userHasAttendanceAddress';
import type {
  JurisdictionIdentifier,
  OrganizationCountrySource,
} from '@src/packages/utils/nationalId';
import {
  getOrganizationCountry,
  needsJurisdictionIdentifier,
} from '@src/packages/utils/nationalId';
import type { ReplacementJourneyImpact } from '@src/packages/workJourney/utils/evaluateReplacementJourneyImpact';
import { persistOrganizationUserJurisdictionId } from '@src/packages/workJourney/utils/persistOrganizationUserJurisdictionId';
import UserRepository from '@src/repositories/UserRepository';
import ExceptionService from '@src/services/ExceptionService';
import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';

import type {
  CoverReplaceModificationForm,
  CoverReplaceStep2Variant,
  TCoverReplaceDurationMode,
  TCoverReplaceStep,
} from '../types';
import { getCoverReplaceStep2Variant } from '../types';
import {
  resolveCoverReplaceExceptionDates,
  toExceptionUserSummary,
} from '../utils/buildShiftReplacementException';
import {
  buildCoverReplaceModificationFormFromRange,
  clampToCoverReplaceAllowedRange,
  isCoverReplaceSubmitOutsideAllowedRange,
  isOutsideCoverReplaceAllowedRange,
  pickAbsenceExceptionForCoverDate,
  resolveCoverReplaceAllowedRange,
  syncCoverReplaceModificationFormToAllowedRange,
  type CoverReplaceAllowedRange,
} from '../utils/resolveCoverReplaceAllowedRange';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

export type UseWorkJourneyCoverAbsenceReplaceOptions = {
  excludeUserIds: string[];
  coverWorkJourneyConfigId?: string;
  coverDate?: string;
  /** Attention-row end date (`YYYY-MM-DD`); defaults to coverDate / +2 days when omitted. */
  coverDateTo?: string | null;
  absentUserId?: string;
  replacedUser?: IWorkJourneyManagementUser | null;
  onRegistered?: () => void | Promise<void>;
};

export type UseWorkJourneyCoverAbsenceReplaceResult = {
  users: IWorkJourneyReplacementCandidate[];
  total: number;
  loading: boolean;
  search: string;
  jobId: string | undefined;
  currentPage: number;
  pageSize: number;
  selectedUser: IWorkJourneyReplacementCandidate | null;
  confirmOpen: boolean;
  confirmLoading: boolean;
  confirmImpact: ReplacementJourneyImpact | null;
  step: TCoverReplaceStep;
  step2Variant: CoverReplaceStep2Variant;
  modificationForm: CoverReplaceModificationForm;
  submitting: boolean;
  setSearch: (value: string) => void;
  setJobId: (value: string | undefined) => void;
  handlePageChange: (page: number, size?: number) => void;
  handleSelectUser: (user: IWorkJourneyReplacementCandidate) => void;
  pendingJurisdictionUser: IWorkJourneyReplacementCandidate | null;
  organizationCountry?: string | null;
  handleConfirmJurisdictionId: (identifier: JurisdictionIdentifier) => Promise<void>;
  handleCancelJurisdictionPrompt: () => void;
  handleContinue: () => Promise<void>;
  handleConfirmSelect: () => void;
  handleChooseAnother: () => void;
  handleBackToStep1: () => void;
  setDurationMode: (mode: TCoverReplaceDurationMode) => void;
  setStartDate: (value: Moment | null) => void;
  setEndDate: (value: Moment | null) => void;
  setComment: (value: string) => void;
  canSubmitStep2: boolean;
  handleRegisterChange: () => Promise<void>;
  /** Inclusive absence window the replace DatePickers may select. */
  allowedRange: CoverReplaceAllowedRange | null;
  /**
   * False when the selectable window comes from an absence exception —
   * permanent cover must not outlive that leave.
   */
  allowPermanent: boolean;
};

/**
 * Loads replacement candidates for the cover-absence replace drawer
 * and drives step-1 confirm + step-2 modification form.
 */
export const useWorkJourneyCoverAbsenceReplace = ({
  excludeUserIds,
  coverWorkJourneyConfigId,
  coverDate,
  coverDateTo,
  absentUserId,
  replacedUser,
  onRegistered,
}: UseWorkJourneyCoverAbsenceReplaceOptions): UseWorkJourneyCoverAbsenceReplaceResult => {
  const intl = useIntl();
  const managementServiceRef = useRef<WorkJourneyManagementService | null>(null);
  if (managementServiceRef.current === null) {
    managementServiceRef.current = new WorkJourneyManagementService();
  }
  const exceptionServiceRef = useRef<ExceptionService | null>(null);
  if (exceptionServiceRef.current === null) {
    exceptionServiceRef.current = new ExceptionService();
  }
  const userRepositoryRef = useRef<UserRepository | null>(null);
  if (userRepositoryRef.current === null) {
    userRepositoryRef.current = new UserRepository();
  }

  const organizationId = useSelector(
    ({ organizations }: { organizations: { organization: { id?: string } } }) =>
      organizations.organization?.id,
  );
  const organizationCountry = useSelector(
    ({
      organizations,
    }: {
      organizations: { organization: OrganizationCountrySource };
    }) => getOrganizationCountry(organizations.organization),
  );

  const [users, setUsers] = useState<IWorkJourneyReplacementCandidate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearchState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [jobId, setJobIdState] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedUser, setSelectedUser] = useState<IWorkJourneyReplacementCandidate | null>(null);
  const [pendingJurisdictionUser, setPendingJurisdictionUser] =
    useState<IWorkJourneyReplacementCandidate | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmImpact, setConfirmImpact] = useState<ReplacementJourneyImpact | null>(null);
  const [step, setStep] = useState<TCoverReplaceStep>(1);
  const [allowedRange, setAllowedRange] = useState<CoverReplaceAllowedRange | null>(() =>
    resolveCoverReplaceAllowedRange({ coverDate, coverDateTo }),
  );
  const [allowedRangeFromAbsence, setAllowedRangeFromAbsence] = useState(false);
  const [modificationForm, setModificationForm] = useState<CoverReplaceModificationForm>(() =>
    buildCoverReplaceModificationFormFromRange(
      resolveCoverReplaceAllowedRange({ coverDate, coverDateTo }),
    ),
  );
  const [submitting, setSubmitting] = useState(false);

  const excludeKey = useMemo(() => excludeUserIds.filter(Boolean).join(','), [excludeUserIds]);
  const step2Variant = getCoverReplaceStep2Variant(confirmImpact);
  // Permanent is only for open-ended staffing (no absence window); impact assign
  // already hides it via step2Variant === 'assign'.
  const allowPermanent = step2Variant === 'replace' && !allowedRangeFromAbsence;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    const loadAllowedRange = async () => {
      let exceptionStartDate: string | null = null;
      let exceptionEndDate: string | null = null;
      const preferredExceptionId = replacedUser?.exceptionId ?? null;
      const absentId = replacedUser?.id || absentUserId;

      if (organizationId && preferredExceptionId) {
        try {
          const exception = await exceptionServiceRef.current!.getExceptionById(
            preferredExceptionId,
            organizationId,
          );
          if (exception?.type !== ExceptionService.TYPES.SHIFT_REPLACEMENT) {
            exceptionStartDate = exception?.startDate ?? null;
            exceptionEndDate = exception?.endDate ?? null;
          }
        } catch {
          // Fall through to user-exception lookup / cover dates.
        }
      }

      if (!exceptionStartDate && organizationId && absentId) {
        try {
          const exceptions = await exceptionServiceRef.current!.getExceptionsByUserId(
            absentId,
            organizationId,
          );
          const picked = pickAbsenceExceptionForCoverDate(
            exceptions,
            coverDate,
            preferredExceptionId,
          );
          exceptionStartDate = picked?.startDate ?? null;
          exceptionEndDate = picked?.endDate ?? null;
        } catch {
          // Keep attention / cover-date fallback.
        }
      }

      if (cancelled) return;

      const nextRange = resolveCoverReplaceAllowedRange({
        coverDate,
        coverDateTo,
        exceptionStartDate,
        exceptionEndDate,
      });
      setAllowedRange(nextRange);
      setAllowedRangeFromAbsence(Boolean(exceptionStartDate && exceptionEndDate));
    };

    void loadAllowedRange();
    return () => {
      cancelled = true;
    };
  }, [
    absentUserId,
    coverDate,
    coverDateTo,
    organizationId,
    replacedUser?.exceptionId,
    replacedUser?.id,
  ]);

  useEffect(() => {
    if (!allowedRange) return;
    setModificationForm(prev =>
      syncCoverReplaceModificationFormToAllowedRange(
        prev,
        allowedRange,
        allowedRangeFromAbsence,
      ),
    );
  }, [allowedRange, allowedRangeFromAbsence]);

  useEffect(() => {
    if (!organizationId || step !== 1) {
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const response = await managementServiceRef.current!.listReplacementCandidates({
          organizationId,
          search: debouncedSearch || undefined,
          jobId,
          excludeUserIds: excludeKey ? excludeKey.split(',') : undefined,
          from: (currentPage - 1) * pageSize,
          pageSize,
        });

        if (cancelled) return;
        setUsers(response.users);
        setTotal(response.total);
      } catch (error) {
        console.error('Error listing replacement candidates:', error);
        if (!cancelled) {
          setUsers([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [organizationId, debouncedSearch, jobId, excludeKey, currentPage, pageSize, reloadKey, step]);

  const setSearch = useCallback((value: string) => {
    setSearchState(value);
  }, []);

  const setJobId = useCallback((value: string | undefined) => {
    setJobIdState(value);
    setCurrentPage(1);
    setReloadKey(key => key + 1);
  }, []);

  const handlePageChange = useCallback((page: number, size?: number) => {
    if (size != null && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
      return;
    }
    setCurrentPage(page);
  }, [pageSize]);

  const handleSelectUser = useCallback(async (user: IWorkJourneyReplacementCandidate) => {
    if (!organizationId) {
      return;
    }
    const orgUser = await userRepositoryRef.current?.getUser(organizationId, user.id);
    if (!userHasAttendanceAddress(orgUser?.address)) {
      notification.error({
        message: intl.formatMessage({ id: 'users.select.tooltip.missingAddress' }),
      });
      return;
    }
    const personalIdCard = orgUser?.idCard || user.idCard || '';
    const existingJurisdictionId = orgUser?.jurisdictionId ?? user.jurisdictionId;
    if (
      needsJurisdictionIdentifier(
        organizationCountry,
        personalIdCard,
        existingJurisdictionId,
        orgUser?.address?.country,
      )
    ) {
      setPendingJurisdictionUser({
        ...user,
        idCard: personalIdCard,
        jurisdictionId: existingJurisdictionId,
        address: orgUser?.address ?? user.address,
      });
      return;
    }
    setSelectedUser(user);
  }, [intl, organizationCountry, organizationId]);

  const handleConfirmJurisdictionId = useCallback(
    async (identifier: JurisdictionIdentifier) => {
      if (!organizationId || !pendingJurisdictionUser) {
        return;
      }
      try {
        const orgUser = await userRepositoryRef.current?.getUser(
          organizationId,
          pendingJurisdictionUser.id,
        );
        if (!orgUser) {
          throw new Error('Organization user not found');
        }
        await persistOrganizationUserJurisdictionId(organizationId, orgUser, identifier);
        setSelectedUser({ ...pendingJurisdictionUser, jurisdictionId: identifier });
        setPendingJurisdictionUser(null);
      } catch (error) {
        console.error('Error saving jurisdiction identifier:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
      }
    },
    [intl, organizationId, pendingJurisdictionUser],
  );

  const handleCancelJurisdictionPrompt = useCallback(() => {
    setPendingJurisdictionUser(null);
  }, []);

  const impactRequestIdRef = useRef(0);

  const handleContinue = useCallback(async () => {
    if (!selectedUser || !organizationId || !coverWorkJourneyConfigId || !coverDate) {
      return;
    }

    const requestId = ++impactRequestIdRef.current;
    setConfirmOpen(true);
    setConfirmLoading(true);
    setConfirmImpact(null);

    try {
      const impact = await managementServiceRef.current!.evaluateCoverReplacementImpact({
        organizationId,
        coverDate,
        coverWorkJourneyConfigId,
        candidate: selectedUser,
      });
      if (requestId !== impactRequestIdRef.current) return;
      setConfirmImpact(impact);
    } catch (error) {
      console.error('Error evaluating replacement impact:', error);
      if (requestId !== impactRequestIdRef.current) return;
      // Never map failures to `no_impact` — that unlocks the permanent replace path.
      setConfirmOpen(false);
      setConfirmImpact(null);
      notification.error({
        message: intl.formatMessage({ id: 'general.save.error.message' }),
      });
    } finally {
      if (requestId === impactRequestIdRef.current) {
        setConfirmLoading(false);
      }
    }
  }, [selectedUser, organizationId, coverWorkJourneyConfigId, coverDate, intl]);

  const handleConfirmSelect = useCallback(() => {
    setConfirmOpen(false);
    setModificationForm(prev => {
      const next = buildCoverReplaceModificationFormFromRange(allowedRange, prev.comment);
      if (
        next.durationMode === 'permanent' &&
        (confirmImpact?.kind === 'impact' || allowedRangeFromAbsence)
      ) {
        next.durationMode = 'range';
      }
      return next;
    });
    setStep(2);
  }, [allowedRange, allowedRangeFromAbsence, confirmImpact?.kind]);

  const handleChooseAnother = useCallback(() => {
    setConfirmOpen(false);
    setConfirmImpact(null);
    setSelectedUser(null);
  }, []);

  const handleBackToStep1 = useCallback(() => {
    setStep(1);
  }, []);

  const setDurationMode = useCallback(
    (mode: TCoverReplaceDurationMode) => {
      setModificationForm(prev => {
        if (mode === 'single') {
          const startDate = clampToCoverReplaceAllowedRange(prev.startDate, allowedRange);
          return {
            ...prev,
            durationMode: mode,
            startDate,
            endDate: startDate,
          };
        }
        if (mode === 'permanent') {
          if (!allowPermanent) {
            return prev;
          }
          return {
            ...prev,
            durationMode: mode,
            startDate: null,
            endDate: null,
          };
        }
        const parseIso = (value?: string | null) => {
          if (!value) return null;
          const parsed = moment(value, 'YYYY-MM-DD', true);
          return parsed.isValid() ? parsed : null;
        };
        const fallbackStart =
          clampToCoverReplaceAllowedRange(prev.startDate, allowedRange) ??
          parseIso(allowedRange?.from) ??
          parseIso(coverDate) ??
          moment();
        const fallbackEnd =
          clampToCoverReplaceAllowedRange(prev.endDate, allowedRange) ??
          parseIso(allowedRange?.to) ??
          fallbackStart.clone();
        return {
          ...prev,
          durationMode: mode,
          startDate: fallbackStart,
          endDate: fallbackEnd.isBefore(fallbackStart, 'day') ? fallbackStart : fallbackEnd,
        };
      });
    },
    [allowPermanent, allowedRange, coverDate],
  );

  const setStartDate = useCallback(
    (value: Moment | null) => {
      const clamped = clampToCoverReplaceAllowedRange(value, allowedRange);
      setModificationForm(prev => ({
        ...prev,
        startDate: clamped,
        endDate:
          prev.durationMode === 'single'
            ? clamped
            : prev.endDate && clamped && prev.endDate.isBefore(clamped, 'day')
              ? clamped
              : clampToCoverReplaceAllowedRange(prev.endDate, allowedRange),
      }));
    },
    [allowedRange],
  );

  const setEndDate = useCallback(
    (value: Moment | null) => {
      setModificationForm(prev => ({
        ...prev,
        endDate: clampToCoverReplaceAllowedRange(value, allowedRange),
      }));
    },
    [allowedRange],
  );

  const setComment = useCallback((value: string) => {
    setModificationForm(prev => ({ ...prev, comment: value }));
  }, []);

  const canSubmitStep2 = useMemo(() => {
    if (!selectedUser) return false;
    const { durationMode, startDate, endDate } = modificationForm;
    if (durationMode === 'permanent') return allowPermanent;
    if (durationMode === 'single') {
      return (
        Boolean(startDate) && !isOutsideCoverReplaceAllowedRange(startDate, allowedRange)
      );
    }
    return Boolean(
      startDate &&
        endDate &&
        !endDate.isBefore(startDate, 'day') &&
        !isOutsideCoverReplaceAllowedRange(startDate, allowedRange) &&
        !isOutsideCoverReplaceAllowedRange(endDate, allowedRange),
    );
  }, [allowPermanent, allowedRange, modificationForm, selectedUser]);

  const handleRegisterChange = useCallback(async () => {
    if (!canSubmitStep2 || !selectedUser || !organizationId || !coverWorkJourneyConfigId) {
      return;
    }

    const resolvedAbsentUserId = absentUserId || replacedUser?.id;
    if (!resolvedAbsentUserId) {
      notification.error({
        message: intl.formatMessage({ id: 'general.save.error.message' }),
      });
      return;
    }

    const dates = resolveCoverReplaceExceptionDates({
      durationMode: modificationForm.durationMode,
      startDate: modificationForm.startDate,
      endDate: modificationForm.endDate,
      coverDate,
    });
    if (!dates) {
      notification.error({
        message: intl.formatMessage({ id: 'general.save.error.message' }),
      });
      return;
    }

    // Defense in depth: never persist outside the absence / cover window.
    // Permanent mode uses a sentinel end date — skip that check.
    if (
      isCoverReplaceSubmitOutsideAllowedRange({
        startDate: dates.startDate,
        endDate: dates.endDate,
        allowedRange,
        durationMode: modificationForm.durationMode,
      })
    ) {
      notification.error({
        message: intl.formatMessage({
          id: 'workJourney.management.cover.replace.dateOutsideAllowedRange',
        }),
      });
      return;
    }

    setSubmitting(true);
    try {
      const [substituteDoc, absentDoc, coverConfig] = await Promise.all([
        userRepositoryRef.current!.getUser(organizationId, selectedUser.id),
        userRepositoryRef.current!.getUser(organizationId, resolvedAbsentUserId),
        managementServiceRef.current!.getWorkJourneyConfigSnapshot(
          organizationId,
          coverWorkJourneyConfigId,
        ),
      ]);

      const userSummary =
        toExceptionUserSummary(substituteDoc ?? selectedUser) ??
        toExceptionUserSummary(selectedUser);
      const replacedUserSummary =
        toExceptionUserSummary(absentDoc ?? replacedUser ?? { id: resolvedAbsentUserId }) ??
        toExceptionUserSummary(replacedUser ?? { id: resolvedAbsentUserId });

      if (!userSummary || !replacedUserSummary) {
        throw new Error('Missing user summaries for shift replacement');
      }

      const comment = modificationForm.comment.trim();
      await exceptionServiceRef.current!.createShiftReplacementException(
        {
          user: userSummary,
          replacedUser: replacedUserSummary,
          startDate: dates.startDate,
          endDate: dates.endDate,
          replacementWorkJourneyConfigId: coverWorkJourneyConfigId,
          replacementWorkShiftId: coverConfig?.workShift?.id,
          description: comment || undefined,
        },
        organizationId,
      );

      notification.success({
        message: intl.formatMessage({ id: 'general.save.successful.message' }),
      });
      await onRegistered?.();
    } catch (error) {
      console.error('Error creating shift replacement exception:', error);
      notification.error({
        message: intl.formatMessage({ id: 'general.save.error.message' }),
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    absentUserId,
    allowedRange,
    canSubmitStep2,
    coverDate,
    coverWorkJourneyConfigId,
    intl,
    modificationForm.comment,
    modificationForm.durationMode,
    modificationForm.endDate,
    modificationForm.startDate,
    onRegistered,
    organizationId,
    replacedUser,
    selectedUser,
  ]);

  return {
    users,
    total,
    loading,
    search,
    jobId,
    currentPage,
    pageSize,
    selectedUser,
    confirmOpen,
    confirmLoading,
    confirmImpact,
    step,
    step2Variant,
    modificationForm,
    submitting,
    setSearch,
    setJobId,
    handlePageChange,
    handleSelectUser,
    pendingJurisdictionUser,
    organizationCountry,
    handleConfirmJurisdictionId,
    handleCancelJurisdictionPrompt,
    handleContinue,
    handleConfirmSelect,
    handleChooseAnother,
    handleBackToStep1,
    setDurationMode,
    setStartDate,
    setEndDate,
    setComment,
    canSubmitStep2,
    handleRegisterChange,
    allowedRange,
    allowPermanent,
  };
};
